import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { isFirstPurchaseProductId } from "@hamlak/shared";
import { ActionButton, HistoryItem, LineTablist, TopNavigation, type HistoryItemTone } from "@hamlak/ui";
import type { Transaction, TransactionType } from "@/api/types";
import { periodText, t, type MessageKey } from "@/i18n/ko";
import { productName, storeKey } from "@/i18n/codes";
import { formatDate, formatDateDot } from "@/lib/formatDate";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { EmptyState } from "@/components/EmptyState";
import { formatMoney } from "@/features/store/money";
import { useTransactions } from "./useTransactions";
import "./me.css";

/** 시안 탭 순서: 사용 내역 | 결제 내역 (?tab 없으면 기본은 purchase 유지) */
const TABS: readonly TransactionType[] = ["usage", "purchase"];
const isTab = (v: string | null): v is TransactionType => (TABS as readonly string[]).includes(v ?? "");

/** 1.17: 아이콘·색·부호는 kind 기준. grant·refund positive / spend·expire neutral / revoke critical. source는 grant 아이콘 분기에만. */
export function toneOf(kind: Transaction["kind"]): HistoryItemTone {
  if (kind === "grant" || kind === "refund") return "positive";
  if (kind === "revoke") return "critical";
  return "neutral";
}

/** 부제(결제 탭만 — 시안 사용 탭 행은 부제 없음): 결제 채널 상태 · 주문번호 · 무료 로트 만료 일시 · 환불 가능 기한(전부 서버 값 표시만). */
/** 서버 label 은 거래 reason 코드(purchase·welcome·turn…). 문구는 i18n 에서 고르고, 모르는 코드는 코드 그대로. */
const LABEL_KEYS: Record<string, MessageKey> = {
  turn: "history.label.turn",
  suggestion: "history.label.suggestion",
  llm_failed: "history.label.llm_failed",
  suggestion_failed: "history.label.suggestion_failed",
  reroll: "history.label.reroll",
  reroll_failed: "history.label.reroll_failed",
  lot_expired: "history.label.lot_expired",
  play_refund: "history.label.play_refund",
  account_deleted: "history.label.account_deleted",
  welcome: "history.label.welcome",
  purchase: "history.label.purchase",
  manual: "history.label.manual",
  coupon: "history.label.coupon",
};
type ModelNames = ReadonlyMap<string, string>;

/** 시안 제목: 상품 보너스 "보너스 브릭 {N}개"(source bonus) · 결제 행 "브릭 {N}개"(meta.productId, 상품 별칭 없음 AC0-263) · 첫 결제 특가(productId `.first`)는 "첫 결제 특가 ({N}브릭)", 턴 "대화 · {모델명}"(meta.model → pricing.models 표시명). 그 외 label 코드 문구. */
function labelOf(tx: Transaction, models: ModelNames): string {
  // 상품 보너스분(AC0-290): 같은 결제의 무료 로트 — 지급·회수 모두 "보너스 브릭 N개"
  if (tx.source === "bonus") return t("history.label.bonus", { bricks: Math.abs(tx.amount) });
  if (tx.meta.productId && isFirstPurchaseProductId(tx.meta.productId)) return t("history.label.firstPurchase", { bricks: Math.abs(tx.amount) });
  if (tx.meta.productId) return productName({ bricks: Math.abs(tx.amount) });
  const model = tx.label === "turn" && tx.meta.model ? models.get(tx.meta.model) : undefined;
  if (model) return t("history.label.turnModel", { model });
  const key = LABEL_KEYS[tx.label];
  return key ? t(key) : tx.label;
}

function subOf(tx: Transaction): string | undefined {
  // 시안 "Google Play · 결제 완료/환불 완료"(App Store · 웹 결제): 결제 채널은 보는 기기가 아니라 거래의 meta.store(서버 purchases.store)
  const store = tx.meta.store;
  if (store && tx.label === "purchase") return t(storeKey("history.status.paid", store));
  // 보너스 지급 행: 무료(30일 소멸)·환불 대상 아님 고지
  if (tx.source === "bonus" && tx.kind === "grant" && tx.meta.expiresAt) return t("history.bonusNotice", { date: formatDate(tx.meta.expiresAt) });
  if (store && tx.label === "play_refund") return t(storeKey("history.status.refunded", store));
  if (tx.meta.orderId) return t("history.orderId", { orderId: tx.meta.orderId });
  if (tx.meta.expiresAt) return t("history.expiresAt", { date: formatDate(tx.meta.expiresAt) });
  if (tx.meta.refundableUntil) return t("history.refundableUntil", { date: formatDate(tx.meta.refundableUntil) });
  return undefined;
}

/** 시안 결제 탭 우측 값 = 결제 금액(통화 코드로 현지화, 환불은 −, 브릭 아이콘 없음·fg-neutral). 금액을 모르는 거래(과거·부분 회수)는 undefined → 브릭 수량 폴백 */
function paidOf(tx: Transaction): string | undefined {
  const { currency, amountMicros } = tx.meta;
  if (!currency || amountMicros === undefined) return undefined;
  return `${tx.amount < 0 ? "−" : ""}${formatMoney({ currency, amountMicros })}`;
}

/**
 * F07-14 결제 내역 · F06-11 사용 내역(1.17). `/me/bricks?tab=purchase|usage`.
 * LineTablist(사용|결제) → (사용 탭) 보유 브릭 요약 → 날짜 헤더별 HistoryItem 무한 스크롤 → 환불 안내 행(pricing.refundWindowDays).
 */
export function BricksHistoryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const tab: TransactionType = isTab(tabParam) ? tabParam : "purchase";
  const refundWindowDays = useConfigStore((s) => s.settings?.pricing.refundWindowDays);
  const pricingModels = useConfigStore((s) => s.settings?.pricing.models);
  const balance = useAuthStore((s) => s.balance);
  const query = useTransactions(tab);
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const models: ModelNames = new Map((pricingModels ?? []).map((m) => [m.id, m.name]));

  // 시안 ListHeader: 기기 로컬 날짜(2026.10.14)가 바뀔 때마다 헤더 (서버 정렬 = 최신순 그대로)
  const rows: ReactNode[] = [];
  let lastDate: string | null = null;
  for (const tx of items) {
    const date = formatDateDot(tx.at);
    if (date !== lastDate) {
      rows.push(<p key={`d-${date}`} className="history__date">{date}</p>);
      lastDate = date;
    }
    const paid = tab === "purchase" ? paidOf(tx) : undefined;
    rows.push(<HistoryItem key={tx.id} label={labelOf(tx, models)} amount={tx.amount} tone={paid ? "neutral" : toneOf(tx.kind)} value={paid} sub={tab === "purchase" ? subOf(tx) : undefined} data-kind={tx.kind} />);
  }

  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasNextPage || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <main className="page" data-testid="history-page">
      <TopNavigation title={t("page.bricks")} right={null} onBack={() => navigate(-1)} />
      <LineTablist<TransactionType>
        items={TABS.map((key) => ({ key, label: t(key === "purchase" ? "history.tab.purchase" : "history.tab.usage") }))}
        value={tab}
        onChange={(key) => setParams({ tab: key }, { replace: true })}
      />
      {tab === "usage" && balance !== null && (
        <div className="history__summaryWrap">
          <div className="history__summary" data-testid="history-balance">
            <span className="history__summaryLabel">{t("history.balance")}</span>
            <span className="history__summaryValue">{t("store.bricks", { bricks: balance })}</span>
          </div>
        </div>
      )}
      {query.isPending ? (
        <div className="page-placeholder" role="status">
          {t("common.loading")}
        </div>
      ) : query.isError && items.length === 0 ? (
        <EmptyState message={t("history.loadError")} action={{ label: t("common.retry"), onClick: () => void query.refetch() }} />
      ) : items.length === 0 ? (
        <EmptyState message={tab === "purchase" ? t("history.empty.purchase") : t("history.empty.usage")} />
      ) : (
        <section className="history__list" data-testid={`history-${tab}`}>
          {rows}
          {query.hasNextPage && (
            <div className="history__more">
              <ActionButton variant="neutralWeak" size="small" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {t("history.more")}
              </ActionButton>
            </div>
          )}
          {query.hasNextPage && <div ref={sentinel} className="history__sentinel" aria-hidden="true" />}
        </section>
      )}
      {tab === "purchase" && refundWindowDays !== undefined && <p className="history__notice">{t("history.refundNotice", { period: periodText(refundWindowDays) })}</p>}
    </main>
  );
}
